import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { UseFormReturn } from '../../../../../../hooks'
import { ContactPerson } from '../../../../types/contactPerson'
import { CustomerForm } from '../../../../types/customerForm'
import { CustomerFormSchema } from '../../types/customerForm/customerFormSchema'
import {
  isContactPersonEmailValid,
  isContactPersonMainFieldsFilled,
} from '../../utils/contactPersons/contactPersonValidation'
import { useContactPersonFormActions } from '../useContactPersonFormActions'
import { updateContactPersonsAtIndex } from './utils/updateContactPersonsAtIndex'
import { updateContactPersonsWithPrimaryContact } from './utils/updateContactPersonsWithPrimaryContact'

interface UseUpdateContactPersonProps {
  contactPersons: ContactPerson[]
  customerForm: UseFormReturn<CustomerFormSchema>
}

export const useUpdateContactPerson = ({ contactPersons, customerForm }: UseUpdateContactPersonProps) => {
  const { t } = useTranslation()
  const { getValues, setError, setValue } = customerForm
  const { clearMainPrimaryContactFields, resetContactFormFields, setMainPrimaryContactFields } =
    useContactPersonFormActions(customerForm)

  const setAsPrimaryContact = useCallback(
    (editedIndex: number, values: CustomerForm) => {
      const { contactEmail, contactFirstName, contactLastName } = values

      const newPrimaryContactData = {
        email: contactEmail,
        firstName: contactFirstName,
        lastName: contactLastName,
        isPrimary: true,
      }

      const updatedContacts = updateContactPersonsWithPrimaryContact({
        contactPersons,
        editedIndex,
        newPrimaryContactData,
        values,
      })

      setValue('contactPersons', updatedContacts, { shouldDirty: true })

      // This must be triggered for both customer types so adding a new primary contact in company view
      // updates the 3 primary contact inputs in individual view
      setMainPrimaryContactFields(newPrimaryContactData)
    },
    [contactPersons, setMainPrimaryContactFields, setValue],
  )

  const setAsRegularContact = useCallback(
    (editedIndex: number, values: CustomerForm) => {
      const wasContactPrimary = contactPersons[editedIndex].isPrimary

      if (wasContactPrimary && !values.isContactPrimary) {
        clearMainPrimaryContactFields()
      }

      const updatedContacts = updateContactPersonsAtIndex(contactPersons, editedIndex, values)

      setValue('contactPersons', updatedContacts, { shouldDirty: true })
    },
    [clearMainPrimaryContactFields, contactPersons, setValue],
  )

  const updateContactPerson = useCallback(
    (contactPersonId: string) => {
      const values = getValues()
      const { contactEmail, contactFirstName, contactLastName, isContactPrimary } = values

      if (!isContactPersonMainFieldsFilled(contactEmail, contactFirstName, contactLastName)) {
        return false
      }

      if (!isContactPersonEmailValid(contactEmail)) {
        setError('contactEmail', {
          message: t('customer_modal.form.validation.invalid_email'),
        })
        return false
      }

      const editedIndex = contactPersons.findIndex((contact) => contact.id === contactPersonId)

      if (editedIndex === -1) {
        return false
      }

      if (isContactPrimary) {
        setAsPrimaryContact(editedIndex, values)
      } else {
        setAsRegularContact(editedIndex, values)
      }

      resetContactFormFields()
      return true
    },
    [contactPersons, getValues, resetContactFormFields, setAsPrimaryContact, setAsRegularContact, setError, t],
  )

  return { updateContactPerson }
}
